<template>
  <SmartLinkItemComponent
    v-if="model"
    :id="model?.system?.id"
    :component="!model?.system?.workflow"
  >
    <div class="partner-list">
      <div class="container pt-0 pb-0">
        <div class="filter-bar">
          <ul class="nav">
            <li
              class="nav-item"
              :class="{ active: selectedView == view.Partners }"
              @click="selectView(view.Partners)"
            >
              <a class="nav-link"> Partners </a>
            </li>
            <li
              class="nav-item"
              :class="{ active: selectedView == view.Modules }"
              @click="selectView(view.Modules)"
            >
              <a class="nav-link"> Modules </a>
            </li>
          </ul>
          <div ref="dropdown" class="dropdown" @click.stop>
            <a class="dropdown-toggle" @click="toggleDropdown">
              <span class="text">Modalities</span>
              <IconChevronDown class="icon" />
            </a>
            <ul v-show="isOpen" class="dropdown-menu">
              <li v-for="taxonomy in taxonomies" :key="taxonomy.codename" class="dropdown-item">
                <div class="form-check">
                  <input
                    :id="taxonomy.codename"
                    v-model="filters.get(taxonomy.codename)!.value"
                    class="form-check-input"
                    type="checkbox"
                    :name="taxonomy.codename"
                    :value="filters.get(taxonomy.codename)!.value"
                    @change="setup()"
                  />
                  <label class="form-check-label" :for="taxonomy.codename">
                    {{ taxonomy.name }}
                  </label>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="content">
          <div class="row">
            <template v-if="selectedView == view.Partners">
              <PartnerCardItem
                v-for="(item, idx) in partners"
                :key="idx"
                :model="item"
                :link="item.link"
              />
            </template>
            <template v-else-if="selectedView == view.Modules">
              <ModuleCardItem
                v-for="(item, idx) in modules"
                :key="idx"
                :model="item"
                :link="item.link"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </SmartLinkItemComponent>
</template>

<script lang="ts" setup>
import {
  contentTypes,
  type ModuleItemModel,
  type PartnerItemModel,
  type PartnerListModel
} from '~~/app/models'
import type { ModuleItemWithLink } from '~~/app/types/ModuleWithLink'
import type { PartnerItemWithLink } from '~~/app/types/PartnerWithLink'
const isOpen = ref(false)
const dropdown = ref<any>(null)

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}
enum view {
  Partners,
  Modules
}

const { getState } = usePartnerState()

const lastAccess = getState()

const selectedView = ref(lastAccess.value == 'module' ? view.Modules : view.Partners)

function selectView(view: view) {
  selectedView.value = view
}

const pageStore = usePageStore()
const modalityFilterStore = useModalityFilter()

const props = defineProps<{ model: PartnerListModel }>()
const source = computed(() =>
  pageStore.getListAs<PartnerItemModel | ModuleItemModel>(props.model.elements.partners.value)
)

const partnersSrc = computed(() =>
  source.value
    .filter((x:any) => x.system.type == contentTypes.partner_item.codename)
    .map((x:any) => x as PartnerItemWithLink)
)
const modulesSrc = computed(() =>
  source.value
    .filter((x:any) => x.system.type == contentTypes.module_item.codename)
    .map((x:any) => x as ModuleItemWithLink)
)

const partners: Ref<PartnerItemWithLink[]> = ref([])
const modules: Ref<ModuleItemWithLink[]> = ref([])

const taxonomiesResponse = await useFetch('/api/taxonomies')
const partnerRoutes = await useFetch(`/api/partners-routes`)
const taxonomies = taxonomiesResponse?.data?.value?.data?.find(
  (x) => (x.system.codename = 'modalities')
)?.terms

const filters: Map<string, Ref<boolean>> = new Map<string, Ref<boolean>>()

taxonomies?.forEach((e) => {
  filters.set(e.codename, ref(false))
})

function setup() {
  modalityFilterStore.set(
    Array.from(filters.entries())
      .filter((x) => x[1].value)
      .map((x) => x[0])
  )

  if (Array.from(filters.entries()).find((x) => x[1].value)) {
    const filteredValues = Array.from(filters.entries())
      .filter((x) => x[1].value)
      .map((x) => x[0])

    partners.value = partnersSrc.value.filter((x:any) =>
      x.elements.modality.value.find((y:any) => filteredValues.includes(y.codename))
    )
    modules.value = modulesSrc.value.filter((x:any) =>
      x.elements.modality.value.find((y:any) => filteredValues.includes(y.codename))
    )
    applyLinks()
    return
  }
  partners.value = partnersSrc.value
  modules.value = modulesSrc.value

  applyLinks()
}

function applyLinks() {
  partners.value.forEach((item) => {
    const routeItm = partnerRoutes.data.value?.data?.find((x) => x.codename == item.system.codename)
    if (routeItm) {
      item.link = routeItm.path
    }
  })

  modules.value.forEach((item) => {
    const routeItm = partnerRoutes.data.value?.data?.find((x) => x.codename == item.system.codename)
    if (routeItm) {
      item.link = routeItm.path
    }
  })
}
setup()

const closeDropdown = (event: Event) => {
  if (dropdown.value && !dropdown.value.contains(event.target)) {
    isOpen.value = false
  }
}
onMounted(() => {
  document.addEventListener('click', closeDropdown)
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown)
})
</script>
<style scoped>
.dropdown-menu {
  display: block;
  position: absolute;
  background: white;
  border: 1px solid #ccc;
  padding: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}
</style>
